import { Component, EventEmitter, Input, OnInit, Output, OnDestroy } from '@angular/core';
import { SharedService } from '@app/core';
import { PriceDiscoveryService } from '../../services/price-discovery.service';
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

interface NewItem {
    itemName: string;
    itemCode: string;
    unit: string;
    rate: number | null;
    isTaxApplicable: boolean;
}

@Component({
    selector: 'app-add-rate-card-items-modal',
    templateUrl: './add-rate-card-items-modal.component.html',
    styleUrls: ['./add-rate-card-items-modal.component.scss']
})
export class AddRateCardItemsModalComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    @Input() supplierId: string = '';
    @Input() entityId: string = '';
    @Output() success = new EventEmitter<void>();
    @Output() close = new EventEmitter<void>();

    isVisible = false;
    isSubmitting = false;
    items: NewItem[] = [];

    constructor(
        private priceDiscoveryService: PriceDiscoveryService,
        private sharedService: SharedService
    ) { }

    ngOnInit(): void {
        this.resetItems();
    }

    resetItems(): void {
        this.items = [];
        for (let i = 0; i < 5; i++) {
            this.items.push({
                itemName: '',
                itemCode: '',
                unit: '',
                rate: null,
                isTaxApplicable: true
            });
        }
    }

    show(): void {
        this.resetItems();
        this.isVisible = true;
        document.body.style.overflow = 'hidden';
    }

    hide(): void {
        this.isVisible = false;
        document.body.style.overflow = '';
        this.close.emit();
    }

    isFormInvalid(): boolean {
        const validItems = this.items.filter(item => item.itemName?.trim() !== '');
        if (validItems.length === 0) return true;

        for (const item of validItems) {
            if (this.isFieldInvalid(item, 'itemName')) return true;
            if (this.isFieldInvalid(item, 'itemCode')) return true;
            if (this.isFieldInvalid(item, 'unit')) return true;
            if (this.isFieldInvalid(item, 'rate')) return true;
        }
        return false;
    }

    isFieldInvalid(item: NewItem, field: string): boolean {
        const isEntryActive = item.itemName?.trim() !== '' || item.unit?.trim() !== '' || (item.rate !== null && item.rate !== undefined);
        if (!isEntryActive && field !== 'itemName') return false;

        switch (field) {
            case 'itemName':
                return !item.itemName || item.itemName.trim().length === 0 || item.itemName.trim().length > 50;
            case 'itemCode':
                return !!item.itemCode && item.itemCode.trim().length > 50;
            case 'unit':
                const unitRegex = /^[a-zA-Z\s]*$/;
                return !item.unit || !item.unit.trim() || !unitRegex.test(item.unit.trim()) || item.unit.trim().length > 10;
            case 'rate':
                return item.rate === null || item.rate === undefined || item.rate < 0 || item.rate > 9999999999;
            default:
                return false;
        }
    }

    onUnitKeyPress(event: KeyboardEvent): void {
        const charCode = event.which ? event.which : event.keyCode;
        const charStr = String.fromCharCode(charCode);
        if (!/^[a-zA-Z\s]*$/.test(charStr)) {
            event.preventDefault();
        }
    }

    onSubmit(): void {
        const validItems = this.items.filter(item => item.itemName.trim() !== '');

        if (validItems.length === 0) {
            this.sharedService.errorAlert('Please add at least one item with a name');
            return;
        }

        const unitRegex = /^[a-zA-Z\s]+$/;
        // Basic validation for rate and unit for items that have a name
        for (const item of validItems) {
            if (item.itemName.trim().length > 50) {
                this.sharedService.errorAlert(`Item name cannot exceed 50 characters: ${item.itemName}`);
                return;
            }
            if (item.itemCode && item.itemCode.trim().length > 50) {
                this.sharedService.errorAlert(`Item code cannot exceed 50 characters for item: ${item.itemName}`);
                return;
            }
            if (item.rate === null || item.rate === undefined) {
                this.sharedService.errorAlert(`Please enter a rate for item: ${item.itemName}`);
                return;
            }
            if (item.rate > 9999999999) {
                this.sharedService.errorAlert(`Rate cannot exceed 10 digits for item: ${item.itemName}`);
                return;
            }
            if (!item.unit.trim()) {
                this.sharedService.errorAlert(`Please enter a unit for item: ${item.itemName}`);
                return;
            }
            if (!unitRegex.test(item.unit.trim())) {
                this.sharedService.errorAlert(`Unit can only contain alphabets for item: ${item.itemName}`);
                return;
            }
        }

        this.isSubmitting = true;
        const payload = {
            supplierId: this.supplierId,
            items: validItems.map(item => ({
                ...item,
                itemName: item.itemName.trim(),
                unit: item.unit.trim()
            }))
        };

        this.priceDiscoveryService.addSupplierRateCardItems(payload, this.entityId).pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
                        this.sharedService.successAlert(res.message);
                        this.isSubmitting = false;
                        this.success.emit();
                        this.hide();
                    }, (error) => {
                        console.error('Error adding items', error);
                        this.sharedService.errorAlert(error);
                        this.isSubmitting = false;
                    });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
